/**
 * screens/ApproverDecided — Approver · "Decided"
 * ──────────────────────────────────────────────────────────────────────────
 * What you have already settled, and the reason given.
 *
 * The reason column is rendered in `#96271C` — the danger family's text colour
 * — because it is a rejection's reason and the requester will have read the same
 * words. Making it visible here is what lets an approver answer "why was mine
 * rejected?" without going and looking it up.
 *
 * Rows are NOT clickable. A decision is final; there is nothing to open.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { useCallback, useMemo } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import {
  Card,
  DataTable,
  EmptyPanel,
  Loading,
  Money,
  PageContainer,
  PageHeader,
  StatusPill,
  type Column,
} from '../../components';
import { neutral, status as statusTones } from '../../theme/colors';
import { sans, size as fontSize, leading } from '../../theme/typography';
import { ellipsisStyle } from '../../theme/webStyle';
import { useAsyncData } from '../../lib/useAsyncData';
import { dateTime } from '../../lib/dates';
import { dotted } from '../../lib/format';
import { approvalState } from '../../lib/status';
import { fetchApprovals } from '../../services/approvals/approvals.service';
import type { Approval } from '../../schemas/approvals';
import { useShell } from '../../context/ShellContext';

export function ApproverDecidedScreen() {
  const { dataVersion } = useShell();

  const load = useCallback(() => fetchApprovals('decided'), []);
  const { data, loading } = useAsyncData(load, [], {
    version: dataVersion,
    errorTitle: 'Could not load your decisions',
  });

  const rows = data ?? [];

  const columns = useMemo<Column<Approval>[]>(
    () => [
      {
        key: 'request',
        header: 'Request',
        track: { fr: 1.8 },
        render: (row) => (
          <View style={styles.cellStack}>
            <Text numberOfLines={1} style={styles.label}>
              {row.label ?? row.kind}
            </Text>
            <Text numberOfLines={1} style={styles.meta}>
              {dotted(row.who, dateTime(row.decided_at ?? row.created_at))}
            </Text>
          </View>
        ),
      },
      {
        key: 'amount',
        header: 'Amount',
        track: { fr: 1 },
        align: 'right',
        render: (row) =>
          row.amount_minor && row.cur ? (
            <Money value={row.amount_minor} cur={row.cur} size={fontSize.bodyLg} align="right" />
          ) : (
            <Text style={styles.meta}>—</Text>
          ),
      },
      {
        key: 'outcome',
        header: 'Outcome',
        track: { fr: 0.8 },
        render: (row) => {
          const state = approvalState(row.state);
          return <StatusPill label={state.label} tone={state.tone} />;
        },
      },
      {
        key: 'reason',
        header: 'Reason',
        track: { fr: 1.6 },
        render: (row) => (
          <Text numberOfLines={3} style={styles.reason}>
            {row.reason ?? '—'}
          </Text>
        ),
      },
    ],
    [],
  );

  if (loading && !data) {
    return (
      <PageContainer width="approver">
        <Loading />
      </PageContainer>
    );
  }

  return (
    <PageContainer width="approver" testID="screen-decided">
      <PageHeader
        title="Decided"
        subtitle="What you have already settled, and the reason given."
      />

      {rows.length === 0 ? (
        <EmptyPanel title="Nothing decided yet" hint="Your decisions will be listed here." />
      ) : (
        <Card>
          {/* No onRowPress: a decision is final, so the row opens nothing. */}
          <DataTable
            columns={columns}
            rows={rows}
            keyExtractor={(row) => row.id}
            density="roomy"
            columnGap={16}
          />
        </Card>
      )}
    </PageContainer>
  );
}

const styles = StyleSheet.create({
  cellStack: { minWidth: 0, gap: 1 },
  label: { ...sans('500'), fontSize: fontSize.body, color: neutral.ink, ...ellipsisStyle },
  meta: { ...sans('400'), fontSize: fontSize.meta, color: neutral.grey600, ...ellipsisStyle },
  reason: {
    ...sans('400'),
    fontSize: fontSize.meta,
    lineHeight: leading(fontSize.meta),
    color: statusTones.danger.text,
  },
});

export default ApproverDecidedScreen;
